/* ============================================================
   AVRATECH — Quote System
   - Maneja lista de cotización vía localStorage
   - Modal de cotización rápida (1 producto)
   - Página /pages/cotizacion (lista completa con form de envío)
   ============================================================ */

const AvratechQuote = {
  STORAGE_KEY: 'avratech_quote_items',

  /* ---------- Storage ---------- */
  getItems() {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.error('AvratechQuote: storage error', e);
      return [];
    }
  },

  saveItems(items) {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
    this.updateFloatingWidget();
  },

  addItem(item) {
    const items = this.getItems();
    const existing = items.find(i => i.variant_id === item.variant_id);
    if (existing) {
      existing.qty = (existing.qty || 1) + (item.qty || 1);
    } else {
      items.push({
        variant_id: item.variant_id,
        product_id: item.product_id,
        title: item.title,
        sku: item.sku,
        url: item.url,
        image: item.image,
        price: item.price,
        qty: item.qty || 1,
        added_at: new Date().toISOString(),
      });
    }
    this.saveItems(items);
    return items;
  },

  removeItem(variantId) {
    const items = this.getItems().filter(i => i.variant_id !== variantId);
    this.saveItems(items);
    return items;
  },

  updateQty(variantId, qty) {
    const items = this.getItems();
    const item = items.find(i => i.variant_id === variantId);
    if (item) item.qty = Math.max(1, parseInt(qty, 10) || 1);
    this.saveItems(items);
    return items;
  },

  clear() {
    localStorage.removeItem(this.STORAGE_KEY);
    this.updateFloatingWidget();
  },

  /* ---------- Floating widget ---------- */
  updateFloatingWidget() {
    const widget = document.querySelector('[data-avra-quote-float]');
    if (!widget) return;
    const count = this.getItems().reduce((sum, i) => sum + (i.qty || 1), 0);
    const countEl = widget.querySelector('[data-avra-quote-count]');
    if (countEl) countEl.textContent = count;
    widget.classList.toggle('is-active', count > 0);
  },

  /* ---------- Modal (single-product quote) ---------- */
  openModal(productData) {
    const modal = document.querySelector('[data-avra-quote-modal]');
    if (!modal) return;
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Pre-fill product info
    const infoEl = modal.querySelector('[data-avra-product-info]');
    if (infoEl) {
      infoEl.innerHTML = `
        <strong>${this.escapeHtml(productData.title)}</strong><br>
        <span class="sku">SKU: ${this.escapeHtml(productData.sku)}</span>
      `;
    }

    // Hidden product fields
    modal.querySelector('[name="product_title"]').value = productData.title;
    modal.querySelector('[name="product_sku"]').value = productData.sku;
    modal.querySelector('[name="product_url"]').value = productData.url;
  },

  closeModal() {
    const modal = document.querySelector('[data-avra-quote-modal]');
    if (modal) {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  },

  /* ---------- Form submit (Shopify /contact) ---------- */
  buildContactBody(items, form) {
    // Cuando es lista completa: items[]
    // Cuando es modal: 1 item
    const linesArr = items.map((it, i) =>
      `${i + 1}. ${it.title} — SKU: ${it.sku} — Cant: ${it.qty || 1} — URL: ${it.url}`
    );

    return `
SOLICITUD DE COTIZACIÓN — AVRATECH

== Cliente ==
Nombre: ${form.name || ''}
Empresa: ${form.empresa || ''}
Email: ${form.email || ''}
Teléfono: ${form.tel || ''}
Ubicación: ${form.location || ''}

== Productos solicitados (${items.length}) ==
${linesArr.join('\n')}

== Comentarios ==
${form.comments || '(sin comentarios)'}

== Meta ==
Fecha: ${new Date().toLocaleString('es-MX')}
User Agent: ${navigator.userAgent}
    `.trim();
  },

  async submitQuote(items, formData, sourceForm) {
    // Build contact body
    const body = this.buildContactBody(items, formData);

    // Use Shopify's contact form endpoint
    const fd = new FormData();
    fd.append('form_type', 'contact');
    fd.append('utf8', '✓');
    fd.append('contact[name]', formData.name);
    fd.append('contact[email]', formData.email);
    fd.append('contact[phone]', formData.tel);
    fd.append('contact[subject]', `Cotización Avratech — ${items.length} productos`);
    fd.append('contact[body]', body);

    try {
      const res = await fetch('/contact', {
        method: 'POST',
        body: fd,
        headers: { 'Accept': 'application/json' },
      });
      // Shopify returns HTML, so we check status only
      if (res.ok || res.status === 302) return { success: true };
      return { success: false, error: `HTTP ${res.status}` };
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  /* ---------- Helpers ---------- */
  escapeHtml(str) {
    if (!str) return '';
    return String(str).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);
  },

  /* ---------- Init ---------- */
  init() {
    // 1. Wire up "Cotizar" button on product page
    document.querySelectorAll('[data-avra-quote-btn]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const data = {
          variant_id: btn.dataset.variantId,
          product_id: btn.dataset.productId,
          title: btn.dataset.title,
          sku: btn.dataset.sku,
          url: btn.dataset.url,
          image: btn.dataset.image,
          price: btn.dataset.price,
          qty: parseInt(document.querySelector('[data-avra-qty-input]')?.value || 1, 10),
        };
        this.addItem(data);
        this.openModal(data);
      });
    });

    // 2. Modal close
    document.querySelectorAll('[data-avra-modal-close]').forEach(btn => {
      btn.addEventListener('click', () => this.closeModal());
    });
    document.querySelector('[data-avra-quote-modal]')?.addEventListener('click', (e) => {
      if (e.target.hasAttribute('data-avra-quote-modal')) this.closeModal();
    });

    // 3. Modal form submit
    const modalForm = document.querySelector('[data-avra-quote-form]');
    if (modalForm) {
      modalForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(modalForm);
        const formData = Object.fromEntries(fd.entries());

        const items = [{
          title: formData.product_title,
          sku: formData.product_sku,
          url: formData.product_url,
          qty: parseInt(formData.qty || 1, 10),
        }];

        const result = await this.submitQuote(items, formData, modalForm);
        const alertEl = modalForm.querySelector('[data-avra-form-alert]');
        if (result.success) {
          alertEl.innerHTML = '<div class="avra-alert avra-alert--success">¡Gracias! Tu solicitud fue enviada. Te contactaremos en menos de 24 horas hábiles.</div>';
          modalForm.querySelectorAll('input:not([type=hidden]), textarea').forEach(i => i.value = '');
          setTimeout(() => this.closeModal(), 3000);
        } else {
          alertEl.innerHTML = `<div class="avra-alert avra-alert--error">No pudimos enviar tu solicitud (${result.error}). Por favor escríbenos directamente o intenta de nuevo.</div>`;
        }
      });
    }

    // 4. Floating widget — initial render
    this.updateFloatingWidget();

    // 5. Floating widget click → cotización page
    document.querySelector('[data-avra-quote-float]')?.addEventListener('click', () => {
      window.location.href = '/pages/cotizacion';
    });

    // 6. Quote page rendering (if we're on it)
    if (document.querySelector('[data-avra-quote-page]')) {
      this.renderQuotePage();
    }
  },

  /* ---------- Quote page renderer ---------- */
  renderQuotePage() {
    const container = document.querySelector('[data-avra-quote-list]');
    const emptyEl = document.querySelector('[data-avra-quote-empty]');
    const formWrap = document.querySelector('[data-avra-quote-form-wrap]');
    const items = this.getItems();

    if (items.length === 0) {
      if (emptyEl) emptyEl.style.display = 'block';
      if (container) container.style.display = 'none';
      if (formWrap) formWrap.style.display = 'none';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';
    if (container) container.style.display = 'flex';
    if (formWrap) formWrap.style.display = 'block';

    container.innerHTML = items.map(item => `
      <div class="avra-quote-item" data-variant="${this.escapeHtml(item.variant_id)}">
        <img class="avra-quote-item__img" src="${this.escapeHtml(item.image || '')}" alt="${this.escapeHtml(item.title)}">
        <div class="avra-quote-item__info">
          <h3><a href="${this.escapeHtml(item.url)}">${this.escapeHtml(item.title)}</a></h3>
          <div class="sku">SKU: ${this.escapeHtml(item.sku)}</div>
        </div>
        <div class="avra-qty-input">
          <button type="button" data-avra-qty-decr>−</button>
          <input type="number" value="${item.qty || 1}" min="1" data-avra-quote-qty>
          <button type="button" data-avra-qty-incr>+</button>
        </div>
        <button class="avra-quote-item__remove" data-avra-quote-remove>Eliminar</button>
      </div>
    `).join('');

    // Wire qty changes
    container.querySelectorAll('[data-avra-quote-qty]').forEach(input => {
      const variantId = input.closest('[data-variant]').dataset.variant;
      input.addEventListener('change', () => this.updateQty(variantId, input.value));
    });
    container.querySelectorAll('[data-avra-qty-incr]').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('input');
        input.value = parseInt(input.value, 10) + 1;
        input.dispatchEvent(new Event('change'));
      });
    });
    container.querySelectorAll('[data-avra-qty-decr]').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('input');
        const newVal = Math.max(1, parseInt(input.value, 10) - 1);
        input.value = newVal;
        input.dispatchEvent(new Event('change'));
      });
    });
    container.querySelectorAll('[data-avra-quote-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        const variantId = btn.closest('[data-variant]').dataset.variant;
        this.removeItem(variantId);
        this.renderQuotePage();
      });
    });

    // Wire form submit
    const form = document.querySelector('[data-avra-quote-page-form]');
    if (form && !form.dataset.wired) {
      form.dataset.wired = '1';
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(form);
        const formData = Object.fromEntries(fd.entries());
        const currentItems = this.getItems();

        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Enviando...'; }

        const result = await this.submitQuote(currentItems, formData, form);
        const alertEl = form.querySelector('[data-avra-form-alert]');

        if (result.success) {
          alertEl.innerHTML = `<div class="avra-alert avra-alert--success">¡Solicitud enviada! Te contactaremos en menos de 24 horas hábiles con tu cotización.</div>`;
          this.clear();
          setTimeout(() => this.renderQuotePage(), 2500);
        } else {
          alertEl.innerHTML = `<div class="avra-alert avra-alert--error">Error al enviar (${result.error}). Intenta de nuevo o escríbenos por WhatsApp.</div>`;
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Enviar solicitud de cotización'; }
        }
      });
    }
  },
};

// Auto-init
document.addEventListener('DOMContentLoaded', () => AvratechQuote.init());
window.AvratechQuote = AvratechQuote;
